import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import SearchInput from '../../shared/chat-bar/search-input';
import { useAppDispatch } from '../../app/hooks';
import {
    tryToAddActionForChat,
    tryToFetchSingleChat,
} from '../../store/brokers/partners/history-chats/historyChatsSlice';
import {
    tryToCreateChatMessage,
    tryToCreateChatUserMessage,
} from '../../store/brokers/partners/chats/ChatsSlice';
import AiMessages from '../../shared/chat-messages/ai-messages';
import ClientMessages from '../../shared/chat-messages/client-messages';
import { useTheme } from '../../theme-context';
import TeamsModal from '../teams/TeamsModal';
import { MEMBER_STATUS } from '../../utils/constants/member-status';
import ReportIssuesModal from '../report-issue/ReportIssueModal';

interface ChatDrawerProps {
    isOpen: boolean;
    action?: any;
    historyId?: string;
    triggerId: string;
    chatData: any;
    onClose(): void;
}

const ChatDrawer: React.FC<ChatDrawerProps> = ({
    isOpen,
    triggerId,
    historyId,
    chatData,
    action,
    onClose,
}) => {
    const dispatch = useAppDispatch();
    const { theme } = useTheme();
    const [chatId, setChatId] = useState<string>();
    const [isTyping, setIsTyping] = useState(false);
    const [allMessages, setAllMessages] = useState<any>();
    const [isWaiting, setIsWaiting] = useState<boolean>(false);
    const [showTeamsModal, setShowTeamsModal] = useState<boolean>(false);
    const [reportModal, setReportModal] = useState<{
        show: boolean;
        messageId: string;
    }>();
    const scrollableRef = useRef<HTMLDivElement | null>(null);
    const bottomRef = useRef<HTMLDivElement | null>(null);
    const audioRef = useRef<
        | HTMLAudioElement
        | { pause: () => void; paused: boolean; currentTime: number }
        | null
    >(null);

    useEffect(() => {
        const timeout = setTimeout(() => {
            bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
        return () => clearTimeout(timeout);
    }, [allMessages, isTyping, isWaiting]);

    const onGettingChatHistory = async () => {
        if (historyId) {
            try {
                const response = await dispatch(
                    tryToFetchSingleChat(historyId),
                ).unwrap();
                const formatData = response?.data?.map((item: any) => {
                    if (item?.agent)
                        return { agent: { ...item, ...item.agent } };
                    else return { user: { ...item, ...item.user } };
                });
                setAllMessages(formatData);
            } catch (err) {
                setAllMessages(chatData || undefined);
            }
        }
    };

   const onGetChatResponse = async (userMessage: any) => {
    const updated = allMessages ? [...allMessages, userMessage] : [userMessage];
    setAllMessages(updated);

    setTimeout(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 50);

    setIsWaiting(true);

    const payload: any = {
        topicId: chatId,
        chatData: {
            message: userMessage?.user?.message,
            files: userMessage?.user?.files || [],
        },
    };

    try {
        if (action?.enum === 'ask_sophia') {
            const response = await dispatch(
                tryToCreateChatUserMessage({
                    message: payload.chatData.message,
                    files: payload.chatData.files,
                    topic: payload.topicId,
                    user: { name: '', email: '', id: '' },
                }),
            ).unwrap();

            const agentReply = {
                agent: {
                    message: response?.data?.message,
                    files: response?.data?.files || [],
                    createdAt: new Date(),
                    _id: response?.data?.agentMessageId,
                    topic: response.data.topic,
                },
            };

            setAllMessages([...updated, agentReply]);
            setChatId(response?.data?.topic);
        } else {
            const response = await dispatch(
                tryToCreateChatMessage(payload),
            ).unwrap();

            const data = response?.data?.chatData;

            // ✅ NEW: if backend decided not to send an agent reply
            // (e.g. "okay i will" after closing), just stop here
            if (!data || (!data.agent && !data.files)) {
                // keep topic if backend sent it
                if (response?.data?.metadata?.topic && !chatId) {
                    setChatId(response.data.metadata.topic);
                }
                return;
            }

            const agentReply = {
                agent: {
                    message: data?.agent?.message,
                    files: data?.agent?.files || data?.files || [],
                    createdAt: data?.agent?.createdAt,
                    _id:
                        response?.data?.metadata?.messageId ||
                        data?.agent?._id,
                    ...(data?.agent || {}),
                },
            };

            setAllMessages([...updated, agentReply]);
        }
    } catch (error) {
        console.error('Failed to send chat message:', error);
    } finally {
        setIsWaiting(false);
    }
};


    const onStartChat = async () => {
        if (historyId) return;
        setIsWaiting(true);

        if (action) {
            const data = {
                actionSelected: action?.enum,
                chatData: {
                    message: action?.text,
                    files: [],
                },
            };
            if (action?.enum === 'ask_sophia') {
                // const response = await dispatch(tryToCreateChatUserMessage({
                // message: 'Hi',
                // files: data.chatData.files,
                // topic: chatId,
                // user: {name: '', email: '', id: ''}
                // })).unwrap();
                setAllMessages([
                    {
                        agent: {
                            message: `Hi, I'm Sofia your Q&A AI Assistant to help with any query you have.`,
                            files: [],
                            createdAt: new Date(),
                            _id: Math.random(),
                        },
                    },
                ]);
            } else {
                const response = await dispatch(
                    tryToCreateChatMessage(data),
                ).unwrap();
                setAllMessages([
                    {
                        agent: {
                            message:
                                response?.data?.metadata?.preActionMessage
                                    ?.agent?.message,
                            files: [],
                            createdAt: new Date(),
                            _id: response?.data?.metadata?.preActionMessage
                                ?.agent?._id,
                        },
                    },
                    {
                        user: {
                            message: action?.text,
                            files: [],
                            createdAt: new Date(),
                        },
                    },
                    {
                        agent: {
                            message: response?.data?.chatData?.agent?.message,
                            files: response?.data?.chatData?.agent?.files || [],
                            createdAt: new Date(),
                            _id: response?.data?.metadata?.messageId,
                        },
                    },
                ]);
                setChatId(response?.data?.metadata.topic);
            }
        } else {
            const response = await dispatch(
                tryToCreateChatMessage({}),
            ).unwrap();
            setAllMessages([
                {
                    agent: {
                        message: response?.data?.chatData?.agent?.message,
                        files: response?.data?.chatData?.agent?.files || [],
                        createdAt: new Date(),
                        _id: response?.data?.metadata?.messageId,
                    },
                },
            ]);
            setChatId(response?.data?.metadata.topic);
        }

        setIsWaiting(false);
    };

    useEffect(() => {
        if (historyId) {
            setChatId(historyId);
            onGettingChatHistory();
        }
    }, [historyId]);

    useEffect(() => {
        if (!historyId) {
            onStartChat();
        }
    }, []);

    useEffect(() => {
        if (chatData && !action && !allMessages) {
            setAllMessages(chatData);
        }
        if (!(chatData && chatData?.length > 0)) {
            onStartChat();
        }
    }, [chatData, action, historyId]);

    const onClickMessageAction = async (action: string, id: string) => {
        if (action === 'report') {
            setReportModal({
                show: true,
                messageId: id,
            });
            return;
        }
        try {
            await dispatch(
                tryToAddActionForChat({ id, data: { action } }),
            ).unwrap();
            const formatAllMessages = allMessages?.map((item: any) => {
                if (item?.agent?._id === id) {
                    const key =
                        action === 'like'
                            ? 'liked'
                            : action === 'dislike'
                            ? 'disliked'
                            : 'favorite';
                    return {
                        agent: { ...item.agent, [key]: !item.agent[key] },
                    };
                }
                return item;
            });
            setAllMessages(formatAllMessages);
        } catch (err) {
            console.error(err);
        }
    };

    const onClickLinks = (type: string) => {
        if (type === 'teams') setShowTeamsModal(true);
    };

    const onCloseLinksModals = () => setShowTeamsModal(false);

    const onSuccessfullyDeleted = (user?: any) => {
        console.log({user})
        onGetChatResponse({
            user: {
                message: `${user}`,
                files: [],
            },
        });
    };

    const onSuccessReport = () => {
        onGetChatResponse({
            user: {
                message: '*Reported an issue.',
                files: [],
            },
        });
        setReportModal({ show: false, messageId: '' });
    };

    const onCloseReportModal = () => {
        setReportModal(undefined);
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    layoutId={triggerId}
                    className="relative w-full h-screen bg-[#F5F5F5] dark:bg-bgColor-darkMode flex flex-col"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{
                        type: 'spring',
                        stiffness: 300,
                        damping: 30,
                    }}>
                    <header className="flex m-auto items-center justify-between p-4 min-w-[90%] max-w-[90%]">
                        <div></div>
                        <div
                            className="flex flex-row items-center cursor-pointer hover:opacity-80"
                            onClick={onClose}>
                            <button className="mr-2 dark:text-primary-dark">
                                Close
                            </button>
                            <img
                                src={
                                    theme === 'dark'
                                        ? '/assets/shared/close-icon-white.svg'
                                        : '/assets/shared/close-icon.svg'
                                }
                                className="object-contain w-[25px]"
                            />
                        </div>
                    </header>

                    <div
                        ref={scrollableRef}
                        className="scrollable-div flex-1 overflow-y-auto px-4 py-2 max-w-[95%] md:max-w-[90%] min-w-[90%] mx-auto border-2 border-gray-200 dark:bg-[#3a3a3a] rounded-xl bg-white">
                        {allMessages?.map((item: any, idx: number) => (
                            <div key={idx}>
                                {item?.agent ? (
                                    <div className="flex items-start">
                                        {(idx === 0 ||
                                            !allMessages[idx - 1]?.agent
                                                ?.message) && (
                                            <div className="mt-4 mr-2">
                                                <img
                                                    src="/assets/agents/sofia-agent.svg"
                                                    className="min-w-[35px] rounded-full"
                                                />
                                            </div>
                                        )}
                                        <AiMessages
                                            text={item.agent.message}
                                            files={item.agent.files}
                                            isNew={
                                                idx === allMessages.length - 1
                                            }
                                            data={item.agent}
                                            setIsTyping={setIsTyping}
                                            isTyping={
                                                isTyping &&
                                                allMessages?.length === idx + 1
                                            }
                                            showActions
                                            considerDarkMode
                                            onClickAction={onClickMessageAction}
                                            onClickLinks={onClickLinks}
                                            audioRef={audioRef}
                                        />
                                    </div>
                                ) : (
                                    <ClientMessages
                                        color="bg-[#E9EAEB] !text-black"
                                        text={item.user.message}
                                        time={item.user.createdAt}
                                        considerDarkMode
                                        files={item.user.files}
                                    />
                                )}
                            </div>
                        ))}

                        {isWaiting && (
                            <div className="flex items-start gap-2 mt-3">
                                <img
                                    src="/assets/agents/sofia-agent.svg"
                                    className="min-w-[35px] rounded-full"
                                />
                                <div className="text-gray-400 py-2 px-3 rounded-xl max-w-[85%] text-sm animate-pulse">
                                    Sofia is preparing your answer...
                                </div>
                            </div>
                        )}

                        <div ref={bottomRef} />
                    </div>

                    <div className="sticky bottom-0 py-4 bg-[#F5F5F5] dark:bg-bgColor-darkMode">
                        <SearchInput
                            placeholder="Ask Sofia for something"
                            chatData={allMessages}
                            chatId={chatId}
                            considerDarkMode
                            onSendingData={onGetChatResponse}
                            isWaiting={isWaiting || isTyping}
                            isAskingSophia={action?.enum === 'ask_sophia'}
                        />
                    </div>
                </motion.div>
            )}

            {showTeamsModal && (
                <TeamsModal
                    openModal={showTeamsModal}
                    status={MEMBER_STATUS.find(
                        (item: any) => item?.value === 'live',
                    )}
                    handleCloseModal={onCloseLinksModals}
                    SuccessfullyDeleted={onSuccessfullyDeleted}
                    onlyOne={true}
                />
            )}
            {reportModal?.show && (
                <ReportIssuesModal
                    openModal={reportModal?.show}
                    messageId={reportModal?.messageId || ''}
                    chatId={chatId || ''}
                    onSuccessReport={onSuccessReport}
                    handleCloseModal={onCloseReportModal}
                />
            )}
        </AnimatePresence>
    );
};

export default ChatDrawer;
